import { createAxiosInstance } from '../utils/axios.js'
import { loadStripe } from '@stripe/stripe-js'
import { STRIPE_PUBLISHABLE_KEY } from '../config'

const api = createAxiosInstance()

let stripePromise = null

const getStripe = async () => {
  if (!STRIPE_PUBLISHABLE_KEY) {
    throw new Error('STRIPE_PUBLISHABLE_KEY is not configured')
  }

  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY)
  }
  return stripePromise
}

export const paymentService = {
  async createPaymentIntent (
    items,
    userId = null,
    shippingAddress = null,
    billingAddress = null,
    discountCode = null,
    deliveryOptionId = null
  ) {
    try {
      const response = await api.post('/api/payment-intent', {
        items,
        user_id: userId,
        shipping_address: shippingAddress,
        billing_address: billingAddress,
        discount_code: discountCode,
        delivery_option_id: deliveryOptionId
      })
      return response.data
    } catch (error) {
      console.error('Failed to create payment intent:', error)
      throw error
    }
  },

  async createFreeCheckout (
    items,
    userId = null,
    shippingAddress = null,
    billingAddress = null,
    discountCode = null,
    deliveryOptionId = null
  ) {
    try {
      const response = await api.post('/api/free-checkout', {
        items,
        user_id: userId,
        shipping_address: shippingAddress,
        billing_address: billingAddress,
        discount_code: discountCode,
        delivery_option_id: deliveryOptionId
      })
      return response.data
    } catch (error) {
      console.error('Failed to create free checkout:', error)
      throw error
    }
  },

  async confirmPayment (clientSecret, paymentMethod) {
    try {
      const stripe = await getStripe()
      if (!stripe) {
        throw new Error('Stripe failed to initialize')
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: paymentMethod
        }
      )

      if (error) {
        throw error
      }

      return paymentIntent
    } catch (error) {
      console.error('Payment confirmation failed:', error)
      throw error
    }
  },

  async confirmPaymentWithElements (clientSecret, elements, cardElement) {
    try {
      const stripe = await getStripe()
      if (!stripe) {
        throw new Error('Stripe failed to initialize')
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: 'Customer'
            }
          }
        }
      )

      if (error) {
        throw error
      }

      return paymentIntent
    } catch (error) {
      console.error('Payment confirmation failed:', error)
      throw error
    }
  },

  async createCheckout (
    items,
    paymentMethod,
    userId = null,
    shippingAddress = null,
    billingAddress = null,
    discountCode = null,
    deliveryOptionId = null
  ) {
    try {
      const response = await api.post('/api/checkout', {
        items,
        user_id: userId,
        shipping_address: shippingAddress,
        billing_address: billingAddress,
        discount_code: discountCode,
        payment_method: paymentMethod,
        delivery_option_id: deliveryOptionId
      })

      return response.data
    } catch (error) {
      console.error('Failed to create checkout:', error)
      console.error('Error response:', error.response?.data)
      console.error('Error status:', error.response?.status)
      throw error
    }
  },

  async completePayPalPayment (orderId) {
    try {
      const response = await api.post('/api/paypal/complete', {
        order_id: orderId
      })
      return response.data
    } catch (error) {
      console.error('Failed to complete PayPal payment:', error)
      throw error
    }
  },

  async getReceiptByPaymentIntent (paymentIntentId) {
    try {
      const response = await api.get(`/api/receipt/${paymentIntentId}`)
      return response.data
    } catch (error) {
      console.error('Failed to get receipt:', error)
      throw error
    }
  },

  async getPaymentStatus (sessionId) {
    try {
      const response = await api.get(`/api/payment-status/${encodeURIComponent(sessionId)}`)
      return response.data
    } catch (error) {
      console.error('Failed to get payment status:', error)
      throw error
    }
  }
}
